import { Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { first } from 'rxjs/operators';

import { PortalAuthService } from './portal-auth.service';
import { PortalApiService } from './portal-api.service';

// Gate for everything behind the login screen.
//
// Being signed in to Firebase is not enough — the account also has to be a
// portal account with at least one customer linked, which only the server can
// say. So the guard loads /portal/me once and caches it as the session; a
// rejection signs the user out rather than dropping them on an empty shell
// that fails every subsequent request.
@Injectable({ providedIn: 'root' })
export class PortalGuard implements CanActivate {

  constructor(
    private afAuth: AngularFireAuth,
    private auth: PortalAuthService,
    private api: PortalApiService,
    private router: Router
  ) { }

  async canActivate(): Promise<boolean> {
    // authState emits null before Firebase restores a persisted session, so
    // wait for the first real emission instead of bouncing to /login on reload.
    const user = await this.afAuth.authState.pipe(first()).toPromise();
    if (!user) {
      this.router.navigate(['/login']);
      return false;
    }

    if (this.auth.session) { return true; }

    try {
      const session: any = await this.api.me();
      if (!session || !session.success) { throw new Error('rejected'); }
      this.auth.setSession(session);
      return true;
    } catch (err: any) {
      const message = (err && err.error && err.error.message) || 'Your account does not have portal access.';
      await this.auth.signOut(false);
      this.router.navigate(['/login'], { queryParams: { reason: message } });
      return false;
    }
  }
}
